import { useEffect, useRef } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { Spinner } from '@fluentui/react-components';
import { useAuth } from './auth';
import { Shell } from './Shell';
import { LoginPage } from './pages/Login';
import { AcceptInvitePage } from './pages/AcceptInvite';
import { ChangePasswordPage } from './pages/ChangePassword';
import { InstancesPage } from './pages/Instances';
import { UsersPage } from './pages/Users';
import { AuditPage } from './pages/Audit';
import { SettingsPage } from './pages/Settings';
import { KnowledgePage } from './pages/Knowledge';

/** The hub's own authorize address to go back to after signing in, or null: anything else (another site, another path) is ignored. */
function authorizeReturn(next: string | null): string | null {
  if (!next || !next.startsWith('/oidc/authorize?')) return null;
  const u = new URL(next, window.location.origin);
  return u.origin === window.location.origin && u.pathname === '/oidc/authorize' ? u.pathname + u.search : null;
}

export function App() {
  const { state, user, isAdmin } = useAuth();
  useEffect(() => {
    document.title = 'Voxshift Hub';
  }, []);

  // Arrived from an installation's "sign in with the hub": once signed in, go back to the hub's authorize address. Only that one path is followed,
  // so this can never send anyone to a place of an attacker's choosing.
  const next = authorizeReturn(new URLSearchParams(window.location.search).get('next'));
  const returning = next !== null;
  // The page's content-security-policy is written for the person who loads it (it lets the page test the addresses of the instances they may open), so
  // after signing in the page is loaded again to get the one that is theirs.
  const wasSignedOut = useRef(false);
  useEffect(() => {
    if (!state) return;
    if (state.state !== 'authed') wasSignedOut.current = true;
    // nosemgrep: javascript.browser.security.open-redirect.js-open-redirect -- `next` is only ever this site's own /oidc/authorize address (authorizeReturn)
    else if (returning && !state.user.mustChangePassword) window.location.replace(next!);
    else if (wasSignedOut.current) window.location.reload();
  }, [state, next, returning]);

  if (!state) return <Spinner style={{ marginTop: 120 }} label="Loading" />;
  // The link in an invitation email. Only while nobody is signed in, and only until the password is chosen (after that the normal sign-in carries on).
  if (state.state === 'anonymous' && window.location.pathname === '/invite') return <AcceptInvitePage />;
  if (state.state !== 'authed' || !user) return <LoginPage />;
  if (user.mustChangePassword) return <ChangePasswordPage forced />;
  if (returning) return <Spinner style={{ marginTop: 120 }} label="Signing you in" />;
  return (
    <Shell>
      <Routes>
        <Route path="/" element={<InstancesPage />} />
        <Route path="/knowledge" element={<KnowledgePage />} />
        <Route path="/people" element={isAdmin ? <UsersPage /> : <Navigate to="/" replace />} />
        <Route path="/settings" element={isAdmin ? <SettingsPage /> : <Navigate to="/" replace />} />
        <Route path="/activity" element={isAdmin ? <AuditPage /> : <Navigate to="/" replace />} />
        <Route path="/password" element={<ChangePasswordPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Shell>
  );
}
